import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { requestInitPlan } from 'partner360/features/landing/api/requestInitPlan'
import { requestLandingSearchPartner } from 'partner360/features/landing/api/requestLandingSearchPartner'
import { getAllPlanningOfferings } from 'partner360/features/landing/helpers/getAllPlanningOfferings'
import { getOfferings } from 'partner360/features/landing/helpers/getOfferings'
import { getProcessesSortedByOfferings } from 'partner360/features/landing/helpers/getProcessesSortedByOfferings'
import { getStatusWithNotEmptyData } from 'partner360/features/landing/helpers/getStatusWithNotEmptyData'
import { mapLandingPartnerFromSearchToLandingPartner } from 'partner360/features/landing/helpers/mapLandingPartnerFromSearchToLandingPartner'
import { mapLandingPartnerFromSearchToLandingProcesses } from 'partner360/features/landing/helpers/mapLandingPartnerFromSearchToLandingProcesses'
import { ILandingPartnerFromSearch } from 'partner360/models/landing/ILandingPartnerFromSearch'
import { ILandingPlanning } from 'partner360/models/landing/ILandingPlanning'
import { LandingCategory } from 'partner360/models/landing/LandingCategory'
import { LandingOffering } from 'partner360/models/landing/LandingOffering'
import { LandingStatus } from 'partner360/models/landing/LandingStatus'

import { requestLandingData } from './api/requestLandingData'
import { PARTNER360_LANDING_NAMESPACE } from './constants'
import { getFoundProcess } from './helpers/getFoundProcess'
import { IActionChangeLandingItem } from './models/actionChangeLandingItem'
import { IActionTurnOffLandingOffering } from './models/actionTurnOffLandingOffering'
import { initialState } from './сonfigs'

const landingState = createSlice({
  name: PARTNER360_LANDING_NAMESPACE,
  initialState,
  reducers: {
    changeLandingSelectedOffering: (state, { payload }: PayloadAction<LandingOffering>) => {
      state.selectedOffering = payload
    },
    changeLandingSelectedPlanning: (state, { payload }: PayloadAction<ILandingPlanning>) => {
      state.selectedPlanning = payload
      state.selectedCategory = LandingCategory.MyPartners
      state.selectedOffering = LandingOffering.Total
      state.selectedStatus = getStatusWithNotEmptyData({
        selectedCategory: state.selectedCategory,
        selectedPlanning: state.selectedPlanning,
        processedPlannings: state.processedPlannings,
        selectedStatus: state.selectedStatus,
      })
    },
    changeLandingSelectedCategory: (state, { payload }: PayloadAction<LandingCategory>) => {
      state.selectedCategory = payload

      state.selectedStatus = getStatusWithNotEmptyData({
        selectedCategory: state.selectedCategory,
        selectedPlanning: state.selectedPlanning,
        processedPlannings: state.processedPlannings,
        selectedStatus: state.selectedStatus,
      })

      const offerings = getOfferings({
        selectedCategory: state.selectedCategory,
        selectedStatus: state.selectedStatus,
        selectedPlanning: state.selectedPlanning,
        processedPlannings: state.processedPlannings,
      })
      if (offerings[0]) {
        state.selectedOffering = offerings[0].Offering
      }
    },
    changeLandingSelectedStatus: (state, { payload }: PayloadAction<LandingStatus>) => {
      state.selectedStatus = payload
      const availableOfferings = getOfferings({
        selectedPlanning: state.selectedPlanning,
        processedPlannings: state.processedPlannings,
        selectedStatus: state.selectedStatus,
        selectedCategory: state.selectedCategory,
      })
      if (
        availableOfferings.length &&
        !availableOfferings.find((el) => el.Offering === state.selectedOffering)
      ) {
        state.selectedOffering = availableOfferings[0].Offering
      }
    },
    changeLandingSearchValue: (state, { payload }: PayloadAction<string>) => {
      state.searchValue = payload
      state.foundProcess = null
      state.isSearchMode = false
      state.isNotSearchData = false
    },
    setLandingSearchMode: (state, { payload }: PayloadAction<boolean>) => {
      state.isSearchMode = payload
    },
    changePartnersForInitPlans: (state, { payload }: PayloadAction<number>) => {
      state.countOfPartnersForInitPlans = payload
    },
    changePlanInitStatus: (state, { payload }: PayloadAction<RequestStatus>) => {
      state.fetchPlanInitStatus = payload
    },
    toggleLandingOffering: (
      state,
      {
        payload: { partnerId, offering, isOfferingActive },
      }: PayloadAction<IActionTurnOffLandingOffering>
    ) => {
      state.processedPlannings = state.processedPlannings.map((el) => {
        if (
          el.partnerId === partnerId &&
          el.planningId === state.selectedPlanning.PlanningId &&
          el.offering === offering
        ) {
          return {
            ...el,
            isOfferingActive: Number(isOfferingActive),
            status: isOfferingActive ? LandingStatus.NotCompleted : LandingStatus.NotApplicable,
          }
        }

        return el
      })
    },

    changeLandingItem: (
      state,
      {
        payload: { partnerId, offering, processData, actionData },
      }: PayloadAction<IActionChangeLandingItem>
    ) => {
      state.processedPlannings = state.processedPlannings.map((el) => {
        if (
          el.partnerId === partnerId &&
          el.planningId === state.selectedPlanning.PlanningId &&
          el.offering === offering
        ) {
          return {
            ...el,
            ...(processData || {}),
            actions: el.actions.map((action) => {
              if (actionData && action.Key === actionData.Key) {
                return { ...action, ...(actionData || {}) }
              }
              return action
            }),
          }
        }

        return el
      })
    },
    onLandingSearch: (state) => {
      state.isSearchMode = true
      state.foundProcess = getFoundProcess({
        searchValue: state.searchValue,
        processedPlannings: state.processedPlannings,
        partners: state.partners,
      })
      if (state.searchValue && state.foundProcess) {
        const foundForOffering = state.processedPlannings.find(
          (el) =>
            el.partnerId === state.foundProcess?.partnerId &&
            el.offering === state.selectedOffering &&
            el.isOfferingActive &&
            el.planningId === state.selectedPlanning.PlanningId
        )

        state.selectedOffering = foundForOffering ? state.selectedOffering : LandingOffering.Total

        const foundForCategory = state.processedPlannings.filter((el) => {
          if (state.selectedOffering === LandingOffering.Total) {
            return (
              el.partnerId === state.foundProcess?.partnerId &&
              el.planningId === state.selectedPlanning.PlanningId
            )
          }

          return (
            el.partnerId === state.foundProcess?.partnerId &&
            el.offering === state.selectedOffering &&
            el.isOfferingActive &&
            el.planningId === state.selectedPlanning.PlanningId
          )
        })
        state.selectedCategory = foundForCategory.some(
          (el) => el.category === state.selectedCategory
        )
          ? state.selectedCategory
          : foundForCategory[0].category

        const foundForStatus = state.processedPlannings.filter((el) => {
          if (state.selectedOffering === LandingOffering.Total) {
            return (
              el.partnerId === state.foundProcess?.partnerId &&
              el.planningId === state.selectedPlanning.PlanningId &&
              el.category === state.selectedCategory
            )
          }

          return (
            el.partnerId === state.foundProcess?.partnerId &&
            el.offering === state.selectedOffering &&
            el.isOfferingActive &&
            el.planningId === state.selectedPlanning.PlanningId &&
            el.category === state.selectedCategory
          )
        })
        state.selectedStatus =
          (foundForStatus.some((el) => el.status === state.selectedStatus)
            ? state.selectedStatus
            : foundForStatus[0].status) || state.selectedStatus

        state.isSearchMode = false
      }
    },
    clearLandingFoundPartners: (state) => {
      state.foundPartners = []
    },
    addLandingPartner: (state, action: PayloadAction<ILandingPartnerFromSearch>) => {
      state.foundPartners = []
      state.partners.push(mapLandingPartnerFromSearchToLandingPartner(action.payload))
      state.processedPlannings.push(
        ...mapLandingPartnerFromSearchToLandingProcesses(
          action.payload,
          state.selectedPlanning,
          state.selectedCategory
        )
      )
      state.selectedCategory = LandingCategory.RequestPartners
      state.selectedStatus = LandingStatus.NotCompleted
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestLandingData.pending, (state, action) => {
        if (action.meta.arg?.isRefreshing) {
          state.refreshLandingStatus = RequestStatus.PENDING
        } else {
          state.fetchStatus = RequestStatus.PENDING
        }
      })
      .addCase(requestLandingData.rejected, (state, action) => {
        if (action.meta.arg?.isRefreshing) {
          state.refreshLandingStatus = RequestStatus.REJECTED
        } else {
          state.fetchStatus = RequestStatus.REJECTED
        }
      })
      .addCase(requestLandingData.fulfilled, (state, action) => {
        if (action.payload) {
          const { processedPlannings, plannings, defaultPlanning, partners } = action.payload
          if (processedPlannings.length) {
            state.partners = partners
            state.plannings = plannings
            const allOfferings = getAllPlanningOfferings(plannings[0])
            state.processedPlannings = getProcessesSortedByOfferings({
              processes: processedPlannings,
              offeringsOrder: allOfferings,
            })

            if (action.meta.arg?.isRefreshing) {
              state.refreshLandingStatus = RequestStatus.FULFILLED
              state.selectedCategory = processedPlannings.some(
                (el) => el.category === state.selectedCategory
              )
                ? state.selectedCategory
                : processedPlannings.filter(
                    (el) => el.planningId === state.selectedPlanning.PlanningId
                  )[0].category
            } else {
              state.selectedPlanning = defaultPlanning

              state.selectedCategory = processedPlannings.filter(
                (el) => el.planningId === state.selectedPlanning.PlanningId
              )[0].category

              state.fetchStatus = RequestStatus.FULFILLED
            }

            state.selectedStatus = getStatusWithNotEmptyData({
              selectedCategory: state.selectedCategory,
              selectedPlanning: state.selectedPlanning,
              processedPlannings: state.processedPlannings,
              selectedStatus: state.selectedStatus,
            })
          } else {
            state.isNoData = true
          }
        } else {
          state.fetchStatus = RequestStatus.REJECTED
        }
      })

      .addCase(requestInitPlan.fulfilled, (state) => {
        state.countOfPartnersWithInitializedPlans += 1
      })

      .addCase(requestLandingSearchPartner.pending, (state) => {
        state.partnerSearchStatus = RequestStatus.PENDING
      })
      .addCase(requestLandingSearchPartner.fulfilled, (state, action) => {
        state.partnerSearchStatus = RequestStatus.FULFILLED
        if (action.payload && action.payload.length) {
          state.foundPartners = action.payload
        } else {
          state.isNotSearchData = true
        }
        state.isSearchMode = false
      })
      .addCase(requestLandingSearchPartner.rejected, (state) => {
        state.partnerSearchStatus = RequestStatus.REJECTED
      })
  },
})

export const {
  changeLandingSelectedOffering,
  changeLandingSelectedPlanning,
  changeLandingSelectedCategory,
  changeLandingSelectedStatus,
  toggleLandingOffering,
  changeLandingItem,
  changeLandingSearchValue,
  setLandingSearchMode,
  onLandingSearch,
  changePartnersForInitPlans,
  changePlanInitStatus,
  clearLandingFoundPartners,
  addLandingPartner,
} = landingState.actions

export default landingState.reducer
